import type { IHasDeliveryAreasCache } from "@/app/caching/caches/deliveryAreasCache";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import type { EnumDayOfWeek } from "@/app/services/generated/enums/enumDayOfWeek.generated";
import type Guid from "@/app/types/common/guid";
import type { Maybe } from "@/components/utils/typeHelpers";
import {
  DayOfWeek,
  LocalDate,
  ChronoUnit,
  TemporalAdjusters,
} from "@js-joda/core";

export function nextOrSameWeekday(
  earliest: LocalDate,
  dayOfWeek: DayOfWeek
): LocalDate {
  return earliest.with(TemporalAdjusters.nextOrSame(dayOfWeek));
}

export function previousOrSameWeekday(
  date: LocalDate,
  dayOfWeek: DayOfWeek
): LocalDate {
  return date.with(TemporalAdjusters.previousOrSame(dayOfWeek));
}

/**
 * Start date to show for a repeat schedule.
 * A weekly schedule (or a new area) uses next Monday, so switching to a
 * multi-week schedule begins next week.
 * A longer schedule uses the next Monday on or after six days ago that
 * falls on the same week of the cycle. A null anchor uses 28 Sept 2026,
 * matching dispatch calculation. Saving that Monday keeps the same deliveries.
 */
export function suggestedRepeatStartDate(
  today: LocalDate,
  repeatWeeks: number,
  repeatStartDate?: LocalDate | null
): LocalDate {
  if (repeatWeeks <= 1) {
    return nextOrSameWeekday(today.plusDays(1), DayOfWeek.MONDAY);
  }
  return findWeekdayMatchingRepeatSchedule(
    DayOfWeek.MONDAY,
    today.minusDays(6),
    repeatWeeks,
    repeatStartDate
  );
}

function findWeekdayMatchingRepeatSchedule(
  dayOfWeek: DayOfWeek,
  earliestDispatch: LocalDate,
  repeatWeeks: number,
  repeatStartDate: Maybe<LocalDate>
): LocalDate {
  const natural = nextOrSameWeekday(earliestDispatch, dayOfWeek);

  if (repeatWeeks <= 1) {
    return natural;
  }

  // Default Monday anchor: Sept 28, 2026
  const baseDate = repeatStartDate ?? LocalDate.of(2026, 9, 28);

  // Duration in days from anchor
  const daysBetween = baseDate.until(natural, ChronoUnit.DAYS);

  // Convert 7-day rolling anchor blocks to a floored week index
  const weeksBetween = Math.floor(daysBetween / 7.0);

  // Mathematical floored modulo guarantee: [0, repeatWeeks - 1]
  const weekOffset = ((weeksBetween % repeatWeeks) + repeatWeeks) % repeatWeeks;

  return weekOffset === 0
    ? natural
    : natural.plusWeeks(repeatWeeks - weekOffset);
}

export function doesDateMatchRepeatSchedule(
  date: LocalDate,
  dispatchDays: readonly EnumDayOfWeek[],
  repeatWeeks: number,
  repeatStartDate: Maybe<LocalDate>
) {
  return (
    !!dispatchDays.some((day) => day.key === date.dayOfWeek().value()) &&
    findWeekdayMatchingRepeatSchedule(
      date.dayOfWeek(),
      date,
      repeatWeeks,
      repeatStartDate
    ).equals(date)
  );
}

//
// Implementation of Despatch Date Calculation based on BE implementation.
//
export function calculateDeliveryAreasDistributionDates(
  deliveryAreaId: Maybe<Guid>,
  allowedDispatchDays: readonly EnumDayOfWeek[],
  cache: IHasDeliveryAreasCache
): { dispatchDate: LocalDate | null; deliveryDate: LocalDate | null } {
  const da = cache.deliveryAreas.getById(deliveryAreaId);
  if (da) {
    const now = tenantService.nowDateTime;
    const date = now.toLocalDate();
    const time = now.toLocalTime();

    const delay =
      (da.isSameDayDispatchCutOffTime ? 0 : 1) +
      (da.dispatchCutOffTime && time >= da.dispatchCutOffTime ? 1 : 0);

    const dateReadyForDispatch = date.plusDays(delay);

    const daDispatchDays = new Set(
      da.despatchDays.map((x) => DayOfWeek.of(x.key))
    );
    let possibleDispatchDays = new Set(
      allowedDispatchDays
        .map((x) => DayOfWeek.of(x.key))
        .filter((day) => daDispatchDays.has(day))
    );
    // If no overlap, use DA as preference
    if (possibleDispatchDays.size === 0) {
      possibleDispatchDays = daDispatchDays;
    }

    // Choose the earliest of each allowed day-of-week, fall back to ready date if none at all.
    const dispatchDate =
      [...possibleDispatchDays]
        .map((day) =>
          findWeekdayMatchingRepeatSchedule(
            day,
            dateReadyForDispatch,
            da.repeatWeeks,
            da.repeatStartDate
          )
        )
        .sort((a, b) => a.compareTo(b))[0] ?? dateReadyForDispatch;
    return {
      dispatchDate,
      deliveryDate: dispatchDate.plusDays(da.leadTimeDays),
    };
  }
  return {
    dispatchDate: null,
    deliveryDate: null,
  };
}
